import React, { memo, useCallback, useMemo } from 'react';

import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';

import { Button, ButtonSizes, CheckBox, RadioButton } from 'components/atoms';
import { Colors } from 'context';
import { ILessonJournal } from './LessonJournal';

interface IStudentsAttendance
  extends Pick<
    ILessonJournal,
    | 'isFinished'
    | 'students'
    | 'onCheckAllStudents'
    | 'onCheckStudent'
    | 'onUncheckAllStudents'
    | 'isAllStudentsChecked'
    | 'joinTimes'
    | 'onMarkJoined'
  > {}

export const StudentsAttendance = memo(
  ({
    students,
    isFinished,
    isAllStudentsChecked,
    joinTimes,
    onMarkJoined,
    onCheckAllStudents,
    onUncheckAllStudents,
    onCheckStudent,
  }: IStudentsAttendance) => {
    const { t } = useTranslation();

    // spec 0013: join-click hints — chip per student + "mark joined" button
    const hasJoinHints = useMemo(
      () => Boolean(joinTimes && Object.keys(joinTimes).length),
      [joinTimes],
    );

    const showStudents = useCallback(
      () =>
        students.map(({ attendanceId, studentId, studentName, checked }, index) => {
          const joinTime = studentId ? joinTimes?.[studentId] : undefined;

          return (
            <StudentRow key={attendanceId}>
              <CheckBoxStyled
                checked={Boolean(checked)}
                text={studentName}
                onClick={() => onCheckStudent(index)}
                disabled={isFinished}
              />
              {joinTime && <JoinChip>{t('lesson.join.joinedAt', { time: joinTime })}</JoinChip>}
            </StudentRow>
          );
        }),
      [students, isFinished, onCheckStudent, joinTimes, t],
    );

    return (
      <StudentsAttendanceContainer>
        <HeaderRow>
          <StudentsAttendanceHeader>
            {t('lesson.journal.checkAttendances')}
          </StudentsAttendanceHeader>
          {hasJoinHints && !isFinished && onMarkJoined && (
            <Button
              variant="outline"
              size={ButtonSizes.sm}
              sidePaddings={16}
              onClick={onMarkJoined}>
              {t('lesson.join.markJoined')}
            </Button>
          )}
        </HeaderRow>
        <RadioButtonWrapper>
          <RadioButtonStyled
            checked={isAllStudentsChecked}
            text={t('lesson.journal.checkOptions.allChecked')}
            disabled={isFinished}
            onClick={onCheckAllStudents}
          />
          <RadioButtonStyled
            checked={!isAllStudentsChecked}
            text={t('lesson.journal.checkOptions.partial')}
            disabled={isFinished}
            onClick={onUncheckAllStudents}
          />
        </RadioButtonWrapper>

        <Separator />

        <StudentsContainer studentsCount={students.length}>{showStudents()}</StudentsContainer>
      </StudentsAttendanceContainer>
    );
  },
);

const StudentsAttendanceContainer = styled.div`
  ${tablet} {
    width: 564px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const StudentsAttendanceHeader = styled.h3`
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
  margin: 0;

  ${tablet} {
    ${fontService.h3()}
  }
`;

const RadioButtonStyled = styled(RadioButton)`
  width: fit-content;
`;

const RadioButtonWrapper = styled.div`
  margin-top: 20px;

  ${RadioButtonStyled}:not(:first-of-type) {
    margin-top: 8px;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.additional_gray};
  margin-top: 14px;
`;

const StudentsContainer = styled.div<{ studentsCount: number }>`
  display: flex;
  margin-top: 18px;
  flex-direction: column;
  flex-wrap: wrap;

  ${tablet} {
    ${({ studentsCount }) => {
      if (studentsCount < 8) {
        return css`
          height: calc(28px * ${studentsCount});
        `;
      }
      return css`
        height: 224px;
      `;
    }}
  }
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  width: fit-content;
`;

const CheckBoxStyled = styled(CheckBox)`
  width: fit-content;
`;

const JoinChip = styled.span`
  padding: 1px 8px;
  border-radius: 10px;
  background-color: ${Colors.additional_gray};
  color: ${Colors.gray_dark};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
